
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import axiosClient from "../../../api/axiosClient";
import tradingEnquiryService from "../services/tradingEnquiryService";

const styles = {
  page: {
    padding: 24,
    background: "#f5f6fa",
    minHeight: "100%",
  },
  panel: {
    maxWidth: 1100,
    margin: "0 auto",
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    overflow: "hidden",
  },
  header: {
    padding: "20px 24px",
    background: "linear-gradient(90deg, #5f0cff, #7b2cff)",
    color: "#fff",
  },
  body: {
    padding: 24,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 18,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 7,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: 7,
    fontSize: 14,
    boxSizing: "border-box",
  },
  sectionTitle: {
    margin: "26px 0 14px",
    fontSize: 16,
    fontWeight: 700,
    color: "#111827",
  },
  button: {
    padding: "10px 18px",
    borderRadius: 7,
    border: "1px solid #d1d5db",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 600,
  },
  primaryButton: {
    padding: "10px 18px",
    borderRadius: 7,
    border: "none",
    background: "#6d28d9",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 600,
  },
};

export default function CreateTradingEnquiryPage() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    business_partner_id: "",
    enquiry_date: new Date().toISOString().slice(0, 10),
    expected_response_date: "",
    priority: "NORMAL",
    remarks: "",
  });

  const [customerSearch, setCustomerSearch] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [materialSearch, setMaterialSearch] = useState("");
  const [materialOptions, setMaterialOptions] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  const [quantity, setQuantity] = useState("1");
  const [itemDescription, setItemDescription] = useState("");
  const [items, setItems] = useState([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const searchCustomers = async (value) => {
    setCustomerSearch(value);
    setSelectedCustomer(null);
    updateForm("business_partner_id", "");

    if (value.trim().length < 2) {
      setCustomerOptions([]);
      return;
    }

    try {
      const response = await axiosClient.get(
        "/api/ppe/masters/business-partner/customers/lookup",
        { params: { search: value.trim() } }
      );

      const data = response.data?.data;
      setCustomerOptions(
        Array.isArray(data) ? data : data?.items || []
      );
    } catch (err) {
      setError("Unable to search customers.");
      setCustomerOptions([]);
    }
  };

  const searchRawMaterials = async (value) => {
    setMaterialSearch(value);
    setSelectedMaterial(null);

    if (value.trim().length < 2) {
      setMaterialOptions([]);
      return;
    }

    try {
      const response = await axiosClient.get(
        "/api/ppe/masters/raw-materials/search",
        { params: { q: value.trim() } }
      );

      const data = response.data?.data;
      setMaterialOptions(Array.isArray(data) ? data : []);
    } catch (err) {
      setError("Unable to search Raw Materials.");
      setMaterialOptions([]);
    }
  };

  const addItem = () => {
    setError("");
    setMessage("");

    if (!selectedMaterial) {
      setError("Select a Raw Material first.");
      return;
    }

    const qty = Number(quantity);

    if (!Number.isFinite(qty) || qty <= 0) {
      setError("Quantity must be greater than zero.");
      return;
    }

    if (
      items.some(
        (item) => String(item.raw_material_id) ===
          String(selectedMaterial.id)
      )
    ) {
      setError("This Raw Material is already in the enquiry.");
      return;
    }

    setItems((prev) => [
      ...prev,
      {
        raw_material_id: selectedMaterial.id,
        uom_id:
          selectedMaterial.issue_uom_id ||
          selectedMaterial.base_uom_id ||
          null,
        material_code: selectedMaterial.material_code,
        material_name: selectedMaterial.material_name,
        uom_name:
          selectedMaterial.issue_uom_name ||
          selectedMaterial.base_uom_name ||
          "",
        quantity: String(qty),
        item_description: itemDescription.trim(),
      },
    ]);

    setSelectedMaterial(null);
    setMaterialSearch("");
    setMaterialOptions([]);
    setQuantity("1");
    setItemDescription("");
  };

  const removeItem = (materialId) => {
    setItems((prev) =>
      prev.filter(
        (item) => String(item.raw_material_id) !== String(materialId)
      )
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!form.business_partner_id) {
      setError("Please select a customer.");
      return;
    }

    if (!form.enquiry_date) {
      setError("Please enter the enquiry date.");
      return;
    }

    if (!items.length) {
      setError("Add at least one Raw Material.");
      return;
    }

    setSaving(true);

    try {
      const response = await tradingEnquiryService.createEnquiry({
        ...form,
        enquiry_module: "TRADING",
        items: items.map((item) => ({
          raw_material_id: item.raw_material_id,
          uom_id: item.uom_id,
          quantity: item.quantity,
          item_description: item.item_description,
        })),
      });

      const result = response.data?.data || response.data;
      const enquiryId = result?.id || result?.enquiry_id;

      if (enquiryId) {
        //navigate(`/trading/enquiries/${enquiryId}`);
        navigate("/trading/enquiries");
      } else {
        setMessage(
          "Enquiry request completed. Check the API response before adding navigation."
        );
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.message ||
        "Failed to create Trading Enquiry."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.panel}>
        <div style={styles.header}>
          <div style={{ fontSize: 21, fontWeight: 700 }}>
            Create Trading Enquiry
          </div>
          <div style={{ fontSize: 13, marginTop: 5, opacity: 0.9 }}>
            Capture customer requirements for raw materials
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.body}>
            {error && (
              <div
                role="alert"
                style={{
                  padding: 12,
                  marginBottom: 16,
                  background: "#fef2f2",
                  color: "#b91c1c",
                  borderRadius: 7,
                }}
              >
                {error}
              </div>
            )}

            {message && (
              <div
                role="status"
                style={{
                  padding: 12,
                  marginBottom: 16,
                  background: "#f0fdf4",
                  color: "#166534",
                  borderRadius: 7,
                }}
              >
                {message}
              </div>
            )}

            <div style={styles.grid}>
              <div style={styles.field}>
                <label style={styles.label}>Customer *</label>
                <input
                  style={styles.input}
                  value={customerSearch}
                  onChange={(e) => searchCustomers(e.target.value)}
                  placeholder="Type at least 2 characters"
                  autoComplete="off"
                />

                {customerOptions.length > 0 && (
                  <select
                    style={styles.input}
                    value={form.business_partner_id}
                    onChange={(e) => {
                      const customer = customerOptions.find(
                        (row) =>
                          String(row.id) === String(e.target.value)
                      );

                      setSelectedCustomer(customer || null);
                      updateForm("business_partner_id", e.target.value);
                      setCustomerSearch(
                        customer?.bp_name ||
                        customer?.business_partner_name ||
                        customer?.name ||
                        ""
                      );
                      setCustomerOptions([]);
                    }}
                  >
                    <option value="">Select customer</option>
                    {customerOptions.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.bp_name ||
                          customer.business_partner_name ||
                          customer.name ||
                          `Customer ${customer.id}`}
                      </option>
                    ))}
                  </select>
                )}

                {selectedCustomer && (
                  <small>
                    Selected:{" "}
                    {selectedCustomer.bp_name ||
                      selectedCustomer.business_partner_name ||
                      selectedCustomer.name}
                  </small>
                )}
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Enquiry Date *</label>
                <input
                  style={styles.input}
                  type="date"
                  value={form.enquiry_date}
                  onChange={(e) =>
                    updateForm("enquiry_date", e.target.value)
                  }
                  required
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Expected Response Date</label>
                <input
                  style={styles.input}
                  type="date"
                  value={form.expected_response_date}
                  onChange={(e) =>
                    updateForm("expected_response_date", e.target.value)
                  }
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Priority</label>
                <select
                  style={styles.input}
                  value={form.priority}
                  onChange={(e) =>
                    updateForm("priority", e.target.value)
                  }
                >
                  <option value="LOW">Low</option>
                  <option value="NORMAL">Normal</option>
                  <option value="HIGH">High</option>
                  <option value="URGENT">Urgent</option>
                </select>
              </div>
            </div>

            <div style={styles.sectionTitle}>Required Materials</div>

            <div style={styles.grid}>
              <div style={styles.field}>
                <label style={styles.label}>Search Raw Material *</label>
                <input
                  style={styles.input}
                  value={materialSearch}
                  onChange={(e) => searchRawMaterials(e.target.value)}
                  placeholder="Search by material code or name"
                  autoComplete="off"
                />

                {materialOptions.length > 0 && (
                  <select
                    style={styles.input}
                    value={selectedMaterial?.id || ""}
                    onChange={(e) => {
                      const material = materialOptions.find(
                        (row) =>
                          String(row.id) === String(e.target.value)
                      );
                      setSelectedMaterial(material || null);
                      setMaterialSearch(
                        material
                          ? `${material.material_code || ""} - ${material.material_name || ""}`
                          : ""
                      );
                      setMaterialOptions([]);
                    }}
                  >
                    <option value="">Select Raw Material</option>
                    {materialOptions.map((material) => (
                      <option key={material.id} value={material.id}>
                        {material.material_code} - {material.material_name}
                        {" "}
                        ({material.issue_uom_name || material.base_uom_name || "UOM not set"})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Quantity *</label>
                <input
                  style={styles.input}
                  type="number"
                  min="0.0001"
                  step="0.0001"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Item Description</label>
                <input
                  style={styles.input}
                  value={itemDescription}
                  onChange={(e) => setItemDescription(e.target.value)}
                  placeholder="Optional specification"
                />
              </div>
            </div>

            <div style={{ marginTop: 14 }}>
              <button
                type="button"
                style={styles.button}
                onClick={addItem}
              >
                + Add Material
              </button>
            </div>

            <div style={{ overflowX: "auto", marginTop: 20 }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                }}
              >
                <thead>
                  <tr style={{ background: "#f9fafb" }}>
                    {["Code", "Material", "Quantity", "UOM", "Description", ""].map(
                      (heading) => (
                        <th
                          key={heading}
                          style={{
                            textAlign: "left",
                            padding: 11,
                            borderBottom: "1px solid #e5e7eb",
                          }}
                        >
                          {heading}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.raw_material_id}>
                      <td style={{ padding: 11, borderBottom: "1px solid #eee" }}>
                        {item.material_code}
                      </td>
                      <td style={{ padding: 11, borderBottom: "1px solid #eee" }}>
                        {item.material_name}
                      </td>
                      <td style={{ padding: 11, borderBottom: "1px solid #eee" }}>
                        {item.quantity}
                      </td>
                      <td style={{ padding: 11, borderBottom: "1px solid #eee" }}>
                        {item.uom_name || "—"}
                      </td>
                      <td style={{ padding: 11, borderBottom: "1px solid #eee" }}>
                        {item.item_description || "—"}
                      </td>
                      <td style={{ padding: 11, borderBottom: "1px solid #eee" }}>
                        <button
                          type="button"
                          style={{ ...styles.button, color: "#b91c1c" }}
                          onClick={() => removeItem(item.raw_material_id)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!items.length && (
                    <tr>
                      <td
                        colSpan={6}
                        style={{
                          padding: 20,
                          textAlign: "center",
                          color: "#6b7280",
                        }}
                      >
                        No materials added yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ ...styles.field, marginTop: 24 }}>
              <label style={styles.label}>Remarks</label>
              <textarea
                style={{ ...styles.input, minHeight: 90, resize: "vertical" }}
                value={form.remarks}
                onChange={(e) => updateForm("remarks", e.target.value)}
                placeholder="Additional customer requirements"
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 24,
              }}
            >
              <button
                type="button"
                style={styles.button}
                onClick={() => navigate(-1)}
                disabled={saving}
              >
                Cancel
              </button>

              <button
                type="submit"
                style={{
                  ...styles.primaryButton,
                  opacity: saving ? 0.7 : 1,
                }}
                disabled={saving}
              >
                {saving ? "Saving..." : "Create Enquiry"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
